import { useState, useEffect, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '@/lib/firebase'
import { AuthContext, AdminAccessDeniedError, type AuthContextValue } from '@/hooks/useAuth'
import { toUser } from '@/lib/normalize'
import type { User } from '@/types'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [userProfile, setUserProfile] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user)
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid))
          setUserProfile(userDoc.exists() ? toUser(userDoc.id, userDoc.data()) : null)
        } catch (error) {
          console.error('Could not load the signed-in profile:', error)
          setUserProfile(null)
        }
      } else {
        setUserProfile(null)
      }
      setLoading(false)
    })
    return unsubscribe
  }, [])

  const signIn = async (email: string, password: string) => {
    const credential = await signInWithEmailAndPassword(auth, email, password)
    const userDoc = await getDoc(doc(db, 'users', credential.user.uid))
    if (!userDoc.exists() || userDoc.data().role !== 'admin') {
      await firebaseSignOut(auth)
      throw new AdminAccessDeniedError()
    }
    setUserProfile(toUser(userDoc.id, userDoc.data()))
  }

  const signOut = async () => {
    await firebaseSignOut(auth)
    setUserProfile(null)
  }

  const isAdmin = userProfile?.role === 'admin'

  const value: AuthContextValue = {
    firebaseUser,
    userProfile,
    loading,
    isAdmin,
    signIn,
    signOut,
  }

  return <AuthContext value={value}>{children}</AuthContext>
}
